import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Spinner, Alert, Button, Card, Badge } from 'react-bootstrap';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import { ForumCategory, fetchCategories, fetchTopics, PaginatedTopicsResponse, ForumTopic, createTopic } from 'services/forumService'; // Import new types/functions
import ForumCategoryList from 'components/forum/ForumCategoryList';
import ForumTopicList from 'components/forum/ForumTopicList'; // Import the new component
import CreateTopicForm from 'components/forum/CreateTopicForm'; // Uncomment import
import { formatDistanceToNow } from 'date-fns';

const ForumPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate(); // Hook for navigation
  const [categories, setCategories] = useState<ForumCategory[]>([]);
  const [isLoadingCategories, setIsLoadingCategories] = useState<boolean>(true);
  const [categoriesError, setCategoriesError] = useState<string | null>(null);

  // State for selected category ID (null means "All Posts")
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(() => {
    const categoryIdParam = searchParams.get('category');
    return categoryIdParam ? parseInt(categoryIdParam, 10) : null;
  });

  // State for topics
  const [topicsData, setTopicsData] = useState<PaginatedTopicsResponse | null>(null);
  const [isLoadingTopics, setIsLoadingTopics] = useState<boolean>(false);
  const [topicsError, setTopicsError] = useState<string | null>(null);

  // State for Create Topic Form visibility
  const [showCreateForm, setShowCreateForm] = useState<boolean>(false);

  // State to trigger topic list refresh
  const [refreshTopicListKey, setRefreshTopicListKey] = useState<number>(0);

  // Fetch categories on initial mount
  useEffect(() => {
    const loadCategories = async () => {
      setIsLoadingCategories(true);
      setCategoriesError(null);
      try {
        const data = await fetchCategories();
        setCategories(data);
      } catch (error: any) {
        setCategoriesError(error.message || 'Failed to load categories.');
      } finally {
        setIsLoadingCategories(false);
      }
    };
    loadCategories();
  }, []);

  // Fetch topics whenever selectedCategoryId OR refresh key changes
  useEffect(() => {
    const loadTopics = async () => {
      setIsLoadingTopics(true);
      setTopicsError(null);
      // Don't clear previous topics immediately for a smoother refresh?
      // setTopicsData(null); 
      try {
        // Fetch topics for the currently selected category (page 1)
        // TODO: Handle pagination state if refreshing while on page > 1
        const data = await fetchTopics(selectedCategoryId, 1); // Fetch page 1 on refresh
        setTopicsData(data);
      } catch (error: any) {
        setTopicsError(error.message || 'Failed to load topics.');
        setTopicsData(null); // Clear data on error
      } finally {
        setIsLoadingTopics(false);
      }
    };

    // Don't attempt to load topics until categories are loaded
    if (!isLoadingCategories) {
        loadTopics();
    }
    // Add refreshTopicListKey to dependencies
  }, [selectedCategoryId, isLoadingCategories, refreshTopicListKey]); 

  // Handle category selection
  const handleSelectCategory = (categoryId: number | null) => {
    setSelectedCategoryId(categoryId);
    setShowCreateForm(false); // Hide form when changing category
    // Update URL search parameters
    if (categoryId === null) {
      searchParams.delete('category');
    } else {
      searchParams.set('category', categoryId.toString());
    }
    setSearchParams(searchParams);
  };

  // Handle successful topic creation
  const handleTopicSubmitSuccess = (newTopicId: number) => {
    setShowCreateForm(false); // Hide the form
    console.log(`Topic ${newTopicId} created successfully.`);
    // Increment the key to trigger the useEffect for fetching topics
    setRefreshTopicListKey(prevKey => prevKey + 1);
    // Optionally, ensure the user is viewing the category the topic was added to?
    // const topicCategory = ??? // Need topic category from create response?
    // if (topicCategory && selectedCategoryId !== topicCategory.id) {
    //    handleSelectCategory(topicCategory.id);
    // } else {
    //    setRefreshTopicListKey(prevKey => prevKey + 1);
    // }
  };

  // Get current category name for display
  const selectedCategoryName = selectedCategoryId
    ? categories.find(c => c.id === selectedCategoryId)?.name
    : 'All Posts';
  const selectedCategoryDescription = selectedCategoryId
    ? categories.find(c => c.id === selectedCategoryId)?.description
    : 'All topics from the community.';

  return (
    <Container fluid="lg" className="mt-4">
      <Row className="mb-3 align-items-center">
        <Col>
          <h2>Community Forum</h2>
          <p className="text-muted">Connect, share, and learn with your data community</p>
        </Col>
        <Col xs="auto">
          {/* Disable button if categories are loading, failed, or topics are loading */}
          <Button 
            onClick={() => setShowCreateForm(!showCreateForm)} 
            disabled={isLoadingCategories || categoriesError !== null || isLoadingTopics}
          >
            {showCreateForm ? 'Cancel' : '+ New Post'}
          </Button>
        </Col>
      </Row>

      {/* Render Create Topic Form conditionally ONLY if categories loaded and exist */}
      {showCreateForm && !isLoadingCategories && categoriesError === null && categories.length > 0 && (
          <Card className="mb-3">
              <Card.Body>
                  <Card.Title>Create New Post</Card.Title>
                  <CreateTopicForm 
                    categories={categories} 
                    selectedCategoryId={selectedCategoryId}
                    onCancel={() => setShowCreateForm(false)} 
                    onSubmitSuccess={handleTopicSubmitSuccess}
                  />
              </Card.Body>
          </Card>
      )}
      {/* Optional: Show a message if trying to post but categories failed */}
      {showCreateForm && (isLoadingCategories || categoriesError !== null || categories.length === 0) && (
           <Alert variant="warning" className="mb-3">
             {isLoadingCategories ? "Loading categories..." : (categoriesError || "Cannot create post: No categories found.")}
           </Alert>
      )}

      <Row>
        {/* Categories Column */}
        <Col md={3} className="mb-3">
          <Card>
            <Card.Header>CATEGORIES</Card.Header>
            {isLoadingCategories ? (
              <Card.Body className="text-center"><Spinner animation="border" size="sm" /></Card.Body>
            ) : categoriesError ? (
              <Card.Body><Alert variant="danger">{categoriesError}</Alert></Card.Body>
            ) : (
              <ForumCategoryList
                categories={categories}
                selectedCategoryId={selectedCategoryId}
                onSelectCategory={handleSelectCategory}
              />
            )}
          </Card>
        </Col>

        {/* Topics Column */}
        <Col md={9}>
          <Card>
            <Card.Header>
              <Row>
                <Col>
                  <h5>{selectedCategoryName || 'Forum Topics'}</h5>
                  {selectedCategoryDescription && <small className="text-muted">{selectedCategoryDescription}</small>}
                </Col>
                {/* Optional: Add Search/Filter controls here */}
              </Row>
            </Card.Header>
            {/* REMOVE Card.Body padding if ForumTopicList uses ListGroup flush */}
            {/* <Card.Body> */}
              {isLoadingTopics ? (
                <div className="text-center p-5"><Spinner animation="border" /></div> // Add padding for spinner
              ) : topicsError ? (
                <Alert variant="danger" className="m-3">{topicsError}</Alert> // Add margin for alert
              ) : topicsData && topicsData.items.length > 0 ? (
                <table className="table table-striped">
                  <tbody>
                      {topicsData.items.map((topic) => (
                          <tr key={topic.id}>
                              <td>
                                  <Link 
                                    to={`/forum/topic/${topic.id}`} 
                                    className="fw-bold d-block mb-1"
                                  >
                                      {topic.title}
                                  </Link>
                                  <small className="text-muted">
                                      By <Link to={`/profile/${topic.author.id}`}>{topic.author.name}</Link> - {formatDistanceToNow(new Date(topic.createdAt), { addSuffix: true })}
                                  </small>
                              </td>
                              <td className="text-end text-nowrap">
                                  {/* Placeholder for reply count */}
                                  {/* <Badge bg="secondary">{topic.replyCount || 0} replies</Badge>  */}
                                  {topic.replyCount !== undefined && 
                                      <Badge bg="secondary" className="me-2">{topic.replyCount} {topic.replyCount === 1 ? 'reply' : 'replies'}</Badge>
                                  }
                                   <small className="text-muted d-block" style={{fontSize: '0.8em'}}> 
                                      Last activity: {topic.lastReplyAt ? formatDistanceToNow(new Date(topic.lastReplyAt), { addSuffix: true }) : 'N/A'}
                                  </small> 
                              </td>
                          </tr>
                      ))}
                  </tbody>
                </table>
              ) : (
                <div className="text-center p-5"> {/* Add padding for message */} 
                  <p className="mb-0">No topics found in this category.</p>
                </div>
              )}
            {/* </Card.Body> */}
             {/* Display Pagination Info (if needed, outside the list) */} 
             {topicsData && topicsData.totalPages > 1 && (
                <Card.Footer className="text-center text-muted">
                    Page {topicsData.page} of {topicsData.totalPages} (Total: {topicsData.total} topics)
                    {/* Add actual pagination controls here */} 
                </Card.Footer>
             )}
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default ForumPage;
